import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import "../styles/Header.css";

const ADMIN_LINKS = [
  { label: "Painel Admin", path: "/admin/dashboard?view=operational" },
  { label: "Pedidos", path: "/admin/orders" },
  { label: "Pagamentos", path: "/admin/payment-issues" },
  { label: "Produtos", path: "/admin/products" },
  { label: "Categorias", path: "/admin/categories" },
  { label: "Estoque", path: "/admin/stock" },
  { label: "Clientes", path: "/admin/customers" },
  { label: "Campanhas", path: "/admin/campaigns" },
  { label: "Carrinhos", path: "/admin/abandoned-carts" },
  { label: "Avaliações", path: "/admin/reviews" },
  { label: "Garantia / RMA", path: "/admin/rma" },
  { label: "Notificações", path: "/admin/notifications" },
  { label: "Logs", path: "/admin/logs" },
  { label: "Ferramentas", path: "/admin/tools" },
];

function resolveActiveAdminPath(pathname) {
  if (pathname.startsWith("/admin/orders")) return "/admin/orders";
  if (pathname.startsWith("/admin/payment-issues")) return "/admin/payment-issues";
  if (pathname.startsWith("/admin/categories")) return "/admin/categories";
  if (pathname.startsWith("/admin/stock")) return "/admin/stock";
  if (pathname.startsWith("/admin/customers")) return "/admin/customers";
  if (
    pathname.startsWith("/admin/create-product") ||
    pathname.startsWith("/admin/products")
  ) {
    return "/admin/products";
  }
  if (pathname.startsWith("/admin/campaigns")) return "/admin/campaigns";
  if (pathname.startsWith("/admin/abandoned-carts")) return "/admin/abandoned-carts";
  if (pathname.startsWith("/admin/reviews")) return "/admin/reviews";
  if (pathname.startsWith("/admin/rma")) return "/admin/rma";
  if (
    pathname.startsWith("/admin/notifications") ||
    pathname.toLowerCase() === "/notificationadmin"
  ) {
    return "/admin/notifications";
  }
  if (pathname.startsWith("/admin/logs")) return "/admin/logs";
  if (pathname.startsWith("/admin/tools")) return "/admin/tools";
  if (pathname.startsWith("/admin/dashboard") || pathname.startsWith("/admin")) {
    return "/admin/dashboard?view=operational";
  }
  return null;
}

const UserMenu = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAdminSubmenuOpen, setIsAdminSubmenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    maxHeight: 400,
    width: 220,
  });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return undefined;

    function updateMenuPosition() {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 220;
      const edge = 8;
      const gap = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let left = rect.right - menuWidth;
      left = Math.min(vw - menuWidth - edge, Math.max(edge, left));

      const top = rect.bottom + gap;
      const maxHeight = Math.max(160, vh - top - edge);

      setMenuPosition({
        top,
        left,
        maxHeight,
        width: menuWidth,
      });
    }

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, isAdminSubmenuOpen]);

  useEffect(() => {
    if (isOpen && user?.isAdmin) {
      setIsAdminSubmenuOpen(true);
    }
  }, [isOpen, user?.isAdmin]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
        setIsAdminSubmenuOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!user) {
    return (
      <a href="/login" className="header-action-btn">
        <img
          src={require("../image/perfil.png")}
          alt="Login"
          width="36"
          height="36"
        />
        <span className="header-action-text">Entrar</span>
      </a>
    );
  }

  function closeMenu() {
    setIsOpen(false);
    setIsAdminSubmenuOpen(false);
  }

  function navigateTo(path) {
    navigate(path);
    closeMenu();
  }

  const handleLogout = () => {
    logout();
    navigate("/");
    closeMenu();
  };

  const activeAdminPath = user.isAdmin
    ? resolveActiveAdminPath(location.pathname)
    : null;
  const onAdminArea = Boolean(activeAdminPath);

  const menuContent = isOpen
    ? createPortal(
        <>
        <button
          type="button"
          className="user-menu-backdrop"
          aria-label="Fechar menu"
          onClick={closeMenu}
        />
        <div
          className="user-menu-dropdown"
          ref={menuRef}
          role="dialog"
          aria-label="Menu da conta"
          style={{
            position: "fixed",
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            width: `${menuPosition.width}px`,
            maxHeight: `${menuPosition.maxHeight}px`,
          }}
        >
          <div className="user-menu-header">
            <p className="user-menu-name">{user.name}</p>
            <p className="user-menu-email">{user.email}</p>
          </div>

          <div className="user-menu-items">
            <a
              href="/minha-conta/pedidos"
              onClick={(e) => {
                e.preventDefault();
                navigateTo("/minha-conta/pedidos");
              }}
              className="user-menu-link"
            >
              📦 Meus Pedidos
            </a>
            <a
              href="/minha-conta/fidelidade"
              onClick={(e) => {
                e.preventDefault();
                navigateTo("/minha-conta/fidelidade");
              }}
              className="user-menu-link"
            >
              🔧 Clube do Técnico
            </a>
            <a
              href="/minha-conta/garantia"
              onClick={(e) => {
                e.preventDefault();
                navigateTo("/minha-conta/garantia");
              }}
              className="user-menu-link"
            >
              🛡️ Garantia / RMA (7 dias)
            </a>
            <a
              href="/minha-conta/privacidade"
              onClick={(e) => {
                e.preventDefault();
                navigateTo("/minha-conta/privacidade");
              }}
              className="user-menu-link"
            >
              🔒 Privacidade e dados
            </a>
            <a
              href="/favoritos"
              onClick={(e) => {
                e.preventDefault();
                navigateTo("/favoritos");
              }}
              className="user-menu-link"
            >
              ⭐ Favoritos
            </a>
          </div>

          {user.isAdmin && (
            <div className="user-menu-section user-menu-admin-section">
              <button
                type="button"
                className={`user-menu-link user-menu-link-admin user-menu-admin-toggle${onAdminArea ? " user-menu-admin-toggle--active" : ""}`}
                onClick={() => setIsAdminSubmenuOpen((open) => !open)}
                aria-expanded={isAdminSubmenuOpen}
                aria-controls="user-menu-admin-submenu"
                aria-current={onAdminArea ? "true" : undefined}
              >
                <span>Admin</span>
                <span
                  className={`user-menu-chevron${isAdminSubmenuOpen ? " user-menu-chevron--open" : ""}`}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>
              {isAdminSubmenuOpen && (
                <div
                  id="user-menu-admin-submenu"
                  className="user-menu-submenu"
                  role="menu"
                >
                  {ADMIN_LINKS.map((item) => {
                    const isActive = activeAdminPath === item.path;

                    return (
                      <a
                        key={item.path}
                        href={item.path}
                        role="menuitem"
                        aria-current={isActive ? "page" : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          navigateTo(item.path);
                        }}
                        className={`user-menu-link user-menu-link-admin user-menu-sublink${isActive ? " user-menu-sublink--active" : ""}`}
                      >
                        {item.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="user-menu-section">
            <button onClick={handleLogout} className="user-menu-button">
              🚪 Sair
            </button>
          </div>
        </div>
        </>,
        document.body,
      )
    : null;

  return (
    <div className="user-menu-wrapper">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="header-action-btn"
        aria-label="Menu do usuário"
        aria-expanded={isOpen}
      >
        <img
          src={require("../image/perfil.png")}
          alt="Perfil"
          width="36"
          height="36"
        />
        <span className="header-action-text">{user.name || "Minha Conta"}</span>
      </button>
      {menuContent}
    </div>
  );
};

export default UserMenu;
